import { NotificationTypePreference, UserDefinedNotification, UserDefinedNotificationConfig } from '@apis/Notification/model';
import { ActionIcon, Alert, Button, Center, Divider, Group, Loader, MantineColor, Modal, Space, Text, Tooltip } from '@mantine/core';
import { FillerSwitch } from '@root/Design/Filler';
import { SettingsWithPreviewContainer } from '@root/Design/Settings';
import { SidePanelContentEl, SidePanelPanelEl, SidePanelTitle, SidePanelToolbarEl } from '@root/Design/SidePanel';
import { AuthenticationService } from '@root/Services/AuthenticationService';
import { useDiMemo } from '@root/Services/DI';
import { EventEmitter, useEvent, useEventValue, useToggle } from '@root/Services/EventEmitter';
import { Logger } from '@root/Services/Logger';
import { NotificationService } from '@root/Services/Notification/NotificationService';
import { UserDefinedNotificationApiService } from '@root/Services/Notification/UserDefinedNotificationApiService';
import { useCallback, useEffect, useState } from 'react';
import { injectable, inject } from 'tsyringe';
import { LiveNotificationContent } from '../Viewer/NotificationLiveContent';
import { NotificationPresetService } from './NotificationDatasourcePresets';
import { NotificationSettingsEditorScope } from './Models';
import { NotificationSettingsEditor } from './NotificationSettingsEditor';
import { INotificationPresetItem } from './Presets/BasePresets';
import { AlertCircle, Copy, Trash, TrashOff } from 'tabler-icons-react';

@injectable()
class NotificationDefinitionSettingsLoaderModel {
    private onSaved?: () => void;
    private origConfig: string | undefined = undefined;

    public hasChanged = false;
    public readonly configChanged = new EventEmitter<UserDefinedNotificationConfig | undefined>(undefined);
    public readonly presets: INotificationPresetItem<any>[] = [];
    public typePref?: NotificationTypePreference;
    public readonly notification = new EventEmitter<UserDefinedNotification | undefined>(undefined);
    public readonly saving = new EventEmitter<boolean>(false);
    public readonly loading = new EventEmitter<boolean>(true);

    public get isReadonly() {
        return !!this.typePref?.Id && this.typePref.UserId !== this.authSvc.user?.Id;
    }

    public constructor(
        @inject(UserDefinedNotificationApiService) private readonly notificationApi: UserDefinedNotificationApiService,
        @inject(NotificationPresetService) private readonly notificationPresetSvc: NotificationPresetService,
        @inject(NotificationService) private readonly toastSvc: NotificationService,
        @inject(Logger) private readonly logger: Logger,
        @inject(AuthenticationService) private readonly authSvc: AuthenticationService
    ) {}

    public init(typePrefId: number | undefined, scope: NotificationSettingsEditorScope, onSaved?: () => void) {
        this.load(typePrefId, scope);
        this.onSaved = onSaved;
    }

    private async load(typePrefId: number | undefined, scope: NotificationSettingsEditorScope) {
        this.loading.emit(true);
        try {
            const typePref = typePrefId ? await this.notificationApi.getNotificationDefinitionById(typePrefId) : null;
            this.typePref = typePref ?? this.createTypePref(scope.config);
            const { datasource, context } = scope;
            this.origConfig = this.getChangeKey();
            this.presets.splice(0, Infinity, ...(await this.notificationPresetSvc.getPresets(datasource)));
        } finally {
            this.loading.emit(false);
        }
    }

    public handleChange = () => {
        this.hasChanged = this.origConfig !== this.getChangeKey();
        this.configChanged.emit(this.typePref?.NotificationDefinition);
    };
    public handleConfigChange = (config: UserDefinedNotificationConfig | undefined) => {
        this.handleChange();
        const notification: UserDefinedNotification | undefined = this.typePref
            ? {
                  NotificationTypePreferenceId: this.typePref.Id ?? 0,
                  NotificationDefinition: config,
              }
            : undefined;
        this.notification.emit(notification);
    };

    public setDisabled = async (disabled: boolean) => {
        const config = this.typePref?.NotificationDefinition;
        if (this.typePref?.Id && config) {
            let changeType = config.Disabled ? 'Re-enable' : 'Disable';
            config.Disabled = disabled;
            if (disabled) {
                try {
                    await this.saveSettings(this.typePref);
                    this.toastSvc.notify('Updated', `Notification has been ${changeType}d.`, 'success', null);

                    return config.Disabled;
                } catch (error) {
                    this.logger.error('Failed to save notification definition', { error });
                    this.toastSvc.notify('Update Failed', `Error: Failed to ${changeType}. Please contact support.`, 'error', null);
                }
            } else {
                this.handleChange();
            }
        }
    };

    public copy = async (callback: ((typePref: NotificationTypePreference) => void) | undefined) => {
        if (!this.typePref) {
            return;
        }

        try {
            const copy = { ...this.typePref, Id: 0 };
            if (copy.NotificationDefinition) {
                const title = copy.NotificationDefinition.Title;
                copy.NotificationDefinition.Title = `${title} (Copy)`;
            }
            if (copy.Recipients) {
                copy.Recipients = copy.Recipients.map((r) => ({ ...r, NotificationTypePreferenceId: 0, Id: 0 }));
            }
            const savedCopy = await this.saveSettings(copy);
            callback?.(savedCopy);
            this.toastSvc.notify('Copied', `Notification was copied. `, 'success', null);
        } catch (error) {
            this.logger.error(`Failed while trying to copy notification definition ${this.typePref.Id}`, { error });
            this.toastSvc.notify(
                'Failed while copying',
                `Error: A failure occurred while saving a copy of this notification. Please contact support.`,
                'error',
                null
            );
        }
    };

    public save = async () => {
        if (!this.typePref) {
            return;
        }

        const { NotificationDefinition } = this.typePref;
        if (!NotificationDefinition) {
            return;
        }

        try {
            this.saving.emit(true);

            await this.saveSettings(this.typePref);

            this.toastSvc.notify('Saved', `Notification settings saved.`, 'success', null);
            this.onSaved?.();
        } catch (error) {
            this.logger.error('Failed to save notification definition', { error });
            this.toastSvc.notify('Save Failed', `Error: Failed to save notification settings. Please contact support.`, 'error', null);
        } finally {
            this.saving.emit(false);
        }
    };

    public isNew() {
        return !this.typePref?.Id;
    }

    public isDisabled() {
        return this.typePref?.NotificationDefinition?.Disabled ?? false;
    }

    private getChangeKey() {
        return !this.typePref
            ? ''
            : this.isReadonly
            ? JSON.stringify([this.typePref?.Recipients])
            : JSON.stringify([this.typePref?.NotificationDefinition, this.typePref?.Recipients]);
    }

    private async saveSettings(typePref: NotificationTypePreference) {
        const recipients = typePref.Recipients ?? [];
        let defId = typePref.Id;
        if (!this.isReadonly || !typePref.Id) {
            const def = await this.notificationApi.saveNotificationDefinition(typePref);
            defId = def.Id;
            typePref.Id = defId;
        }
        if (defId && recipients.length) {
            await this.notificationApi.saveNotificationRecipients(defId, recipients);
        }
        return typePref;
    }

    private createTypePref(definition?: UserDefinedNotificationConfig): NotificationTypePreference {
        return {
            NotificationType: 'UserDefined',
            UserId: this.authSvc.user?.Id,
            Recipients: [{ UserId: this.authSvc.user?.Id, MessageType: ['InApp', 'Email'], NotificationTypePreferenceId: 0, Type: 'SingleInclude' }],
            NotificationDefinition: definition,
        };
    }
}

interface NotificationDefinitionSettingsEditorProps {
    typePrefId?: number;
    onFinish: (changed: boolean) => void;
    onCopy?: (typePref: NotificationTypePreference) => void;
    scope: NotificationSettingsEditorScope;
    settingsWidth: number;
}
export function NotificationDefinitionSettingsLoader(props: NotificationDefinitionSettingsEditorProps) {
    const { typePrefId, onCopy, onFinish, scope, settingsWidth } = props;
    const model = useDiMemo(NotificationDefinitionSettingsLoaderModel);
    useEffect(() => {
        model.init(typePrefId, scope, () => onFinish(true));
    }, [model, typePrefId, scope]);
    const loading = useEventValue(model.loading);

    const DynamicNotificationViewer = useCallback(() => {
        const config = useEventValue(model.configChanged);
        return !config ? (
            <Center>
                <Text italic color="dimmed">
                    Preview unavailable
                </Text>
            </Center>
        ) : (
            <LiveNotificationContent config={config} context={scope.context} />
        );
    }, [model.configChanged]);

    return (
        <SettingsWithPreviewContainer
            title="Preview"
            preview={
                <FillerSwitch loading={loading} noDataMessage="Preview unavailable">
                    {() => <DynamicNotificationViewer />}
                </FillerSwitch>
            }
            previewTransparent={false}
            previewScrollable
            settings={
                <SidePanelPanelEl>
                    <SidePanelTitle title="Notification Settings" />
                    <SidePanelContentEl>
                        <FillerSwitch loading={loading} noData={!model.typePref}>
                            {() => (
                                <NotificationSettingsEditor
                                    onConfigChanged={model.handleConfigChange}
                                    onRecipientsChanged={model.handleChange}
                                    typePref={model.typePref!}
                                    presets={model.presets}
                                    scope={scope}
                                    readonly={model.isReadonly}
                                />
                            )}
                        </FillerSwitch>
                    </SidePanelContentEl>

                    <NotficationSettingsToolbar model={model} onFinish={onFinish} onCopy={onCopy} />
                </SidePanelPanelEl>
            }
            settingsWidth={settingsWidth}
            previewType="stack"
        />
    );
}

interface NotificationSettingsToolbarProps {
    onFinish: (changed: boolean) => void;
    onCopy?: (typePref: NotificationTypePreference) => void;
    model: NotificationDefinitionSettingsLoaderModel;
}
function NotficationSettingsToolbar({ model, onCopy, onFinish }: NotificationSettingsToolbarProps) {
    const onDisabled = useCallback(() => onFinish(true), [onFinish]);
    const handleCancel = useCallback(() => onFinish(false), [onFinish]);
    const [copying, setCopying] = useState(false);
    const handleCopy = useCallback(async () => {
        if (model.typePref) {
            setCopying(true);
            model.copy(onCopy).finally(() => setCopying(false));
        }
    }, [model.typePref, onCopy]);
    const saving = useEventValue(model.saving);
    const canSave = !saving && (model.hasChanged || model.isNew());
    useEvent(model.configChanged);

    return (
        <>
            <NotificationToolbarBanner model={model} />
            <SidePanelToolbarEl>
                <Group position="apart" sx={{ flex: 1 }}>
                    {model.isNew() || !model.typePref ? (
                        <div></div>
                    ) : (
                        <>
                            {copying ? (
                                <Loader size="sm" />
                            ) : (
                                <Tooltip label="Create a copy of this notification" position="top">
                                    <ActionIcon color="primary" onClick={handleCopy}>
                                        <Copy />
                                    </ActionIcon>
                                </Tooltip>
                            )}
                        </>
                    )}
                    <Group>
                        <Button disabled={!canSave} onClick={model.save} leftIcon={!saving ? null : <Loader color="gray.0" size="sm" />}>
                            {model.isNew() ? 'Save Notification' : 'Save Changes'}
                        </Button>
                        <Button onClick={handleCancel} variant="outline">
                            {model.isNew() || model.hasChanged ? 'Cancel' : 'Close'}
                        </Button>
                        {model.isNew() || model.isReadonly ? null : <NotificationDisabledState onDisabled={onDisabled} model={model} />}
                    </Group>
                </Group>
            </SidePanelToolbarEl>
        </>
    );
}

function NotificationToolbarBanner({ model }: { model: NotificationDefinitionSettingsLoaderModel }) {
    let title = '';
    let description = '';
    if (model.isReadonly) {
        title = 'Settings are read-only';
        description = 'Notification is owned by another user, however you may opt-out or create an editable copy.';
    } else if (model.isDisabled()) {
        title = 'Notification is disabled';
        description = 'This notification is currently disabled and will not be sent to recipients.';
    }
    return !title ? (
        <></>
    ) : (
        <>
            <Divider />
            <Alert icon={<AlertCircle size={16} />} title={title} color="warning">
                {description}
            </Alert>
        </>
    );
}

function NotificationDisabledState({ model, onDisabled }: { model: NotificationDefinitionSettingsLoaderModel; onDisabled: () => void }) {
    const [opened, { close, open }] = useToggle(false);
    const confirmDisable = useCallback(async () => {
        await model.setDisabled(true);
        close();
        onDisabled();
    }, [onDisabled, model.typePref?.Id, close]);

    const handleDisableClick = useCallback(async () => {
        if (model.isDisabled()) {
            await model.setDisabled(false);
        } else {
            open();
        }
    }, [model.typePref?.Id, model]);

    const isDisabled = model.isDisabled();
    const tooltip = !isDisabled ? 'Disable notification' : 'Re-enable notification';
    const color: MantineColor = !isDisabled ? 'error' : 'success';
    const Icon = isDisabled ? TrashOff : Trash;

    return (
        <>
            <Tooltip label={tooltip} position="top">
                <ActionIcon color={color} onClick={handleDisableClick}>
                    <Icon />
                </ActionIcon>
            </Tooltip>
            <Modal opened={opened} onClose={close} title="Disable Notification">
                <Text size="sm">Are you sure you want to disable this notification? It will no longer be sent to recipients.</Text>
                <Space h="md" />
                <Group position="right">
                    <Button variant="outline" onClick={close}>
                        Cancel
                    </Button>
                    <Button onClick={confirmDisable} color="error">
                        Yes, Disable
                    </Button>
                </Group>
            </Modal>
        </>
    );
}
